import { useState, useEffect, useCallback, useMemo } from 'react';
import { FiCheckSquare, FiUsers, FiCalendar, FiEdit3, FiFileText } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import logger from '../../utils/logging/logger';
import styles from './InstructorDashboard.module.css';

export default function useInstructorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    coursesTaught: 0,
    totalStudents: 0,
    attendanceSessions: 0,
    averageAttendance: 0
  });

  const [taughtCourses, setTaughtCourses] = useState([]);
  const [showInactiveCourses, setShowInactiveCourses] = useState(false);
  const [pendingExtensions, setPendingExtensions] = useState(0);

  const fetchInstructorData = useCallback(async () => {
    try {
      const [statsResponse, coursesResponse, extResponse] = await Promise.allSettled([
        apiService.getInstructorStats(),
        apiService.getCourses(),
        apiService.getPendingExtensionCount(),
      ]);

      if (statsResponse.status === 'fulfilled' && statsResponse.value?.success && statsResponse.value.stats) {
        setStats(statsResponse.value.stats);
      }

      if (coursesResponse.status === 'fulfilled' && coursesResponse.value?.success && coursesResponse.value.courses) {
        // Filter only courses taught by this instructor
        const taught = coursesResponse.value.courses.filter(course => course.instructor_id === user.id);
        setTaughtCourses(taught);
      }

      if (extResponse.status === 'fulfilled' && extResponse.value?.success) {
        setPendingExtensions(extResponse.value.count || 0);
      }
    } catch (error) {
      logger.error('Error fetching instructor data', { error });
      setStats({
        coursesTaught: 0,
        totalStudents: 0,
        attendanceSessions: 0,
        averageAttendance: 0
      });
      setTaughtCourses([]);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchInstructorData();
    }
  }, [user, fetchInstructorData]);

  // Ensure page starts at top on mobile when component mounts
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, []);

  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleCreateSession = () => {
    navigate('/admin/attendance');
  };

  const handleManageStudents = () => {
    navigate('/admin/courses');
  };

  const quickActions = [
    {
      id: 'attendance',
      title: 'Attendance',
      description: 'Set up a new attendance session',
      icon: FiCheckSquare,
      onClick: handleCreateSession
    },
    {
      id: 'students',
      title: 'Students',
      description: 'View enrolled students and assign students',
      icon: FiUsers,
      onClick: handleManageStudents
    },
    {
      id: 'reports',
      title: 'Reports',
      description: 'View attendance statistics',
      icon: FiCalendar,
      onClick: () => navigate('/admin/attendance')
    },
    {
      id: 'content',
      title: 'Content',
      description: 'Modify course content and settings',
      icon: FiEdit3,
      onClick: () => navigate('/admin/courses')
    },
    {
      id: 'assignments',
      title: 'Assignments',
      description: 'Create and manage course assignments',
      icon: FiFileText,
      onClick: () => navigate('/assignments')
    },
    {
      id: 'quizzes',
      title: 'Quizzes',
      description: 'Create quizzes and view submissions',
      icon: FiEdit3,
      onClick: () => navigate('/instructor/quizzes')
    }
  ];

  const getStatIconClass = (color) => {
    switch (color) {
      case 'primary': return styles.statIconPrimary;
      case 'success': return styles.statIconSuccess;
      case 'warning': return styles.statIconWarning;
      case 'accent': return styles.statIconAccent;
      default: return styles.statIconPrimary;
    }
  };

  // Filter courses - show active by default, include inactive when toggle is on
  const filteredCourses = useMemo(() => {
    return taughtCourses.filter(course => {
      const isActive = course.is_active === 1 || course.is_active === true;
      if (showInactiveCourses) {
        // Show all courses (active and inactive)
        return true;
      }
      // Show only active courses
      return isActive;
    });
  }, [taughtCourses, showInactiveCourses]);

  return {
    user,
    navigate,
    stats,
    taughtCourses,
    showInactiveCourses,
    setShowInactiveCourses,
    pendingExtensions,
    handleCourseClick,
    quickActions,
    getStatIconClass,
    filteredCourses
  };
}
